import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  listCashCounters,
  openCounterSession,
  getInterimScroll,
  printInterimScroll,
  createFinalScroll,
  finalizeScroll,
  toBillingError,
} from "./billing-api";

// ─────────────────────────────────────────────────────────────────────────────
// Cash Counter state machine (readmd/ipd-cash-counter-flow.md, spec Part G).
//
//   G1 open session → G3 interim scroll → G4 print interim
//                   → G5 final scroll   → G6 finalize & close
//
// counter_code / session_id / scroll_id are persisted per-admission. There is no
// "fetch open session" endpoint, so without this a page reload mid-shift strands
// the cashier: the UI forgets session_id and re-opening 409s.
// ─────────────────────────────────────────────────────────────────────────────

// Counter codes come from GET /ipd/billing/cash-counters/ at runtime; this is only
// a last-resort fallback if that list can't be loaded.
export const COUNTER_CODES = ["COUNTER-01", "COUNTER-02", "COUNTER-03"];

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function pick(obj, keys, fallback = 0) {
  if (!obj) return fallback;
  for (const k of keys) if (obj[k] != null) return obj[k];
  return fallback;
}

/** Error-handling map from the flow doc; falls back to the shared billing mapper. */
export function toScrollError(err) {
  const raw = String(err?.message || "").toLowerCase();
  if (err?.status === 403 || raw.includes("missing billing permission")) {
    return "You do not have permission for this action.";
  }
  if (raw.includes("already open") || (raw.includes("session") && raw.includes("already"))) {
    return "Session already open for this counter/user.";
  }
  if (raw.includes("already finalized") || (raw.includes("scroll") && raw.includes("finaliz"))) {
    return "This scroll is already finalized.";
  }
  return toBillingError(err);
}

const MODES = [
  { key: "CASH",   keys: ["cash", "CASH"],                           label: "Cash" },
  { key: "UPI",    keys: ["upi", "UPI"],                             label: "UPI" },
  { key: "CARD",   keys: ["card", "CARD"],                           label: "Card" },
  { key: "BANK",   keys: ["bank_transfer", "BANK_TRANSFER", "bank"], label: "Bank" },
  { key: "CHEQUE", keys: ["cheque", "CHEQUE"],                       label: "Cheque" },
];

// A by_mode entry is either a bare amount ("1500.00") or an object ({ amount, count }).
function modeEntry(entry) {
  if (entry == null) return { amount: 0, count: 0 };
  if (typeof entry === "object") {
    return {
      amount: num(pick(entry, ["amount", "total"], 0)),
      count: num(pick(entry, ["count", "txn_count"], 0)),
    };
  }
  return { amount: num(entry), count: 0 };
}

function normalizeTxn(t, i) {
  return {
    id: t.payment_id || t.id || `txn-${i}`,
    receipt: t.payment_number || t.receipt_number || t.number || "—",
    mode: t.payment_mode || t.mode || "—",
    type: t.payment_type || t.type || "—",
    amount: num(t.amount ?? t.paid_amount ?? t.net_amount),
    time: t.payment_time || t.created_at || t.time || null,
    collectedBy: t.cashier_name || t.collected_by || t.cashier || "—",
  };
}

/** Interim-scroll response → view model. Defensive: the endpoint isn't normalized upstream. */
export function normalizeScroll(raw) {
  if (!raw) return null;
  const totals = raw.totals || raw.summary || raw;
  const byMode =
    raw.by_mode || raw.mode_totals || raw.totals_by_mode ||
    totals.by_mode || totals.mode_totals || totals.totals_by_mode || {};
  const txns = raw.transactions || raw.payments || raw.items || raw.entries || [];

  const receiptCount = num(pick(totals, ["receipt_count", "receipts_count", "payment_count", "receipts"]));
  const receiptAmount = num(pick(totals, ["receipt_amount", "receipts_amount", "total_amount", "collected", "total"]));
  const refundCount = num(pick(totals, ["refund_count", "refunds_count", "refunds"]));
  const refundAmount = num(pick(totals, ["refund_amount", "refunds_amount"]));
  const depositCount = num(pick(totals, ["deposit_count", "deposits_count", "deposits"]));
  const depositAmount = num(pick(totals, ["deposit_amount", "deposits_amount"]));

  return {
    sessionId: raw.session_id || totals.session_id || null,
    counterCode: raw.counter_code || raw.counter || null,
    openedAt: raw.opened_at || raw.session_opened_at || null,
    openedBy: raw.opened_by || raw.opened_by_name || raw.cashier || null,
    status: String(raw.status || raw.scroll_status || raw.session_status || "").toUpperCase(),
    receiptCount,
    receiptAmount,
    refundCount,
    refundAmount,
    depositCount,
    depositAmount,
    netCollection: num(pick(totals, ["net_collection", "net_amount", "net"], receiptAmount - refundAmount)),
    modes: MODES.map((m) => ({ key: m.key, label: m.label, ...modeEntry(pick(byMode, m.keys, null)) })),
    transactions: (Array.isArray(txns) ? txns : []).map(normalizeTxn),
  };
}

/** Declared cash is reconciled against the CASH mode total only. */
export const systemCashOf = (scroll) => scroll?.modes.find((m) => m.key === "CASH")?.amount || 0;

// Persistence is keyed by COUNTER (not by patient/admission): a cash counter
// session belongs to a counter + cashier, so it must survive a patient switch.
//
// Stored in localStorage — there is NO endpoint to recover a session_id, so an
// OPEN session must outlive tab close / browser restart and is never dropped. A
// CLOSED (finalized) session is removed, since it has no recoverable value.
// Keyed by user so one cashier can never restore another cashier's session_id.
function ccUser() {
  try {
    return sessionStorage.getItem("userName") || sessionStorage.getItem("username") || "anon";
  } catch {
    return "anon";
  }
}
const selectedKey = () => `ipd_cash_counter_selected_${ccUser()}`;
const counterKey = (code) => `ipd_cash_counter_c_${ccUser()}_${code}`;

function readCounter(code) {
  if (!code) return {};
  try {
    const rec = JSON.parse(localStorage.getItem(counterKey(code))) || {};
    // A finalized session has no recoverable value — treat the counter as fresh.
    if (rec.session?.status === "CLOSED") return {};
    return rec;
  } catch {
    return {};
  }
}

// Persist an OPEN session's record. An OPEN session is never dropped (no endpoint
// to recover its id); a CLOSED one is removed via removeCounter, not written here.
function writeCounter(code, record) {
  if (!code || !record || !(record.session || record.finalScrollId)) return;
  try {
    localStorage.setItem(counterKey(code), JSON.stringify(record));
  } catch {
    /* storage full / disabled */
  }
}

function removeCounter(code) {
  if (!code) return;
  try { localStorage.removeItem(counterKey(code)); } catch { /* ignore */ }
}

function readSelectedCounter() {
  try {
    return localStorage.getItem(selectedKey()) || null;
  } catch {
    return null;
  }
}

/**
 * The current user's OPEN cash-counter session, read straight from localStorage
 * (no hook mount required). Payment payloads tag themselves with this — per
 * readmd/ipd-cash-counter-flow.md G2 — so the payment counts toward that
 * session's interim/final scroll totals. Returns null when no session is open.
 */
export function getActiveCashCounterSession() {
  const code = readSelectedCounter();
  const rec = readCounter(code);
  if (!rec.session?.id) return null;
  return {
    sessionId: rec.session.id,
    counterCode: rec.session.counterCode || code,
  };
}

export function useCashCounter() {
  // Lazy initializers — the last-selected counter, then that counter's stored
  // session. Empty until the counter list resolves (which supplies the default).
  const [counterCode, setCounterCode] = useState(() => readSelectedCounter() || "");
  const [session, setSession] = useState(() => readCounter(counterCode).session || null); // { id, status, openedAt, openedBy }
  const [finalScrollId, setFinalScrollId] = useState(() => readCounter(counterCode).finalScrollId || null);
  const [counters, setCounters] = useState([]); // [{ code, name }]
  const [scroll, setScroll] = useState(null);
  const [printJob, setPrintJob] = useState(null); // { scroll, session, counterCode, at } — drives window.print()
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const retryRef = useRef(null); // { fn, successMsg } of the last failed call

  // G0 — load the cashier's assignable counters and adopt the server default
  // when nothing is selected/persisted yet.
  //
  // The list also carries occupancy, so it is re-fetched whenever a session opens
  // or closes: a counter is exclusive, and this is what tells the *other* cashiers
  // it has been taken (or released).
  const loadCounters = useCallback(async () => {
    try {
      const { counters: list, defaultCode } = await listCashCounters();
      setCounters(list);
      setCounterCode((cur) => cur || defaultCode || list[0]?.code || "");
      return list;
    } catch {
      return null; // selector falls back to the persisted / fallback codes
    }
  }, []);

  useEffect(() => { loadCounters(); }, [loadCounters]);

  // Reload / second browser: the server says this cashier already holds the
  // selected counter but localStorage has no session_id. Adopt the server's —
  // otherwise the exclusivity lock below strands its own owner with no way back in.
  useEffect(() => {
    if (session?.id || !counterCode) return;
    const mine = counters.find((c) => c.code === counterCode && c.openedByMe && c.sessionId);
    if (!mine) return;
    setSession({
      id: mine.sessionId,
      status: "OPEN",
      openedAt: mine.openedAt,
      openedBy: mine.openedBy,
      counterCode: mine.code,
    });
  }, [counters, counterCode, session?.id]);

  // Occupancy of the selected counter. `lockedByOther` is the exclusivity gate:
  // another cashier holds an OPEN session, so this user may not open it.
  const activeCounter = counters.find((c) => c.code === counterCode) || null;
  const lockedByOther = Boolean(activeCounter?.isOpen && !activeCounter.openedByMe && !session?.id);
  const lockedByLabel = activeCounter?.openedBy || null;

  // Persist the active session + scroll id under its counter, and remember the
  // selected counter. Keyed by counter, so an admission change never drops it.
  useEffect(() => {
    if (!counterCode) return;
    try { localStorage.setItem(selectedKey(), counterCode); } catch { /* ignore */ }
    // Once finalized, drop the stored session so a reload starts fresh; otherwise
    // keep the OPEN session durable.
    if (session?.status === "CLOSED") removeCounter(counterCode);
    else writeCounter(counterCode, { session, finalScrollId });
  }, [counterCode, session, finalScrollId]);

  // Switch counter: atomically load that counter's persisted session so the
  // persist effect above never writes one counter's session under another's key.
  const selectCounter = useCallback((code) => {
    const rec = readCounter(code);
    setCounterCode(code);
    setSession(rec.session || null);
    setFinalScrollId(rec.finalScrollId || null);
    setScroll(null);
    setError("");
  }, []);

  const run = useCallback(async (fn, successMsg) => {
    setBusy(true);
    setError("");
    try {
      const data = await fn();
      if (successMsg) toast.success(successMsg);
      retryRef.current = null;
      return { ok: true, data };
    } catch (err) {
      const msg = toScrollError(err);
      setError(msg);
      toast.error(msg);
      retryRef.current = { fn, successMsg };
      return { ok: false, error: msg };
    } finally {
      setBusy(false);
    }
  }, []);

  const retry = useCallback(() => {
    const last = retryRef.current;
    if (!last) return Promise.resolve({ ok: false });
    return run(last.fn, last.successMsg);
  }, [run]);

  const canRetry = Boolean(error) && Boolean(retryRef.current);

  const openSession = useCallback(
    async ({ openingBalance, remarks } = {}) => {
      // Client-side half of the exclusivity rule — the server 409 is the real
      // guard, this just avoids a pointless round-trip and gives a better message.
      if (lockedByOther) {
        const msg = `${counterCode} is already open${lockedByLabel ? ` by ${lockedByLabel}` : " by another user"}.`;
        setError(msg);
        toast.error(msg);
        return { ok: false, error: msg };
      }
      const r = await run(
        () => openCounterSession(counterCode, { openingBalance, remarks }),
        "Counter session opened."
      );
      if (r.ok) {
        const d = r.data || {};
        setSession({
          id: d.session_id || d.id,
          status: String(d.session_status || d.status || "OPEN").toUpperCase(),
          openedAt: d.opened_at || new Date().toISOString(),
          openedBy: d.opened_by || d.opened_by_name || null,
          counterCode: d.counter_code || counterCode,
        });
        setFinalScrollId(null);
        setScroll(null);
      }
      // Either way, re-read occupancy: on success to mark this counter taken, on
      // a 409 to surface who actually holds it.
      loadCounters();
      return r;
    },
    [counterCode, lockedByOther, lockedByLabel, loadCounters, run]
  );

  const refreshInterim = useCallback(async () => {
    if (!session?.id) {
      setError("Open a session first.");
      return { ok: false };
    }
    const r = await run(() => getInterimScroll(counterCode, session.id));
    if (r.ok) setScroll(normalizeScroll(r.data));
    return r;
  }, [counterCode, session, run]);

  // Auto-load interim totals when a session is open but the totals aren't loaded
  // yet — so the summary appears without the cashier pressing Refresh. Fires once
  // per session (deps don't change after the fetch), so it can't loop.
  useEffect(() => {
    if (session?.status === "OPEN" && session?.id && !scroll && !busy) {
      refreshInterim();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, session?.status]);

  const printInterim = useCallback(async () => {
    if (!session?.id) return { ok: false };
    const r = await run(
      () => printInterimScroll(counterCode, session.id),
      "Interim scroll sent to print."
    );
    if (r.ok) {
      // Print the loaded interim totals (what's on screen); fall back to any
      // snapshot the print endpoint returns. A fresh object each time so the
      // page's print effect re-fires even for repeat prints.
      const printable = scroll || normalizeScroll(r.data);
      if (printable) setPrintJob({ scroll: printable, session, counterCode, at: Date.now() });
    }
    return r;
  }, [counterCode, session, scroll, run]);

  // G6 print — no endpoint of its own (unlike the interim scroll): the final
  // scroll is printed from the totals already on screen plus the reconciliation
  // the cashier entered. Mounting the print sheet is what makes window.print()
  // produce anything at all — the rest of the page is .ipd-scroll-no-print.
  const printFinal = useCallback(
    (reconciliation) => {
      if (!scroll) return;
      setPrintJob({ scroll, session, counterCode, kind: "FINAL", reconciliation, at: Date.now() });
    },
    [counterCode, session, scroll]
  );

  const generateFinal = useCallback(
    async ({ declaredCash, remarks } = {}) => {
      if (!session?.id) {
        setError("Open a session first.");
        return { ok: false };
      }
      const r = await run(
        () => createFinalScroll(counterCode, { sessionId: session.id, declaredCash, remarks }),
        "Final scroll generated."
      );
      if (r.ok) setFinalScrollId(r.data?.scroll_id || r.data?.id || null);
      return r;
    },
    [counterCode, session, run]
  );

  const finalize = useCallback(
    async ({ declaredCash, closingRemarks }) => {
      if (!session?.id) {
        setError("Open a session first.");
        return { ok: false };
      }
      if (!finalScrollId) {
        setError("Generate the final scroll first.");
        return { ok: false };
      }
      // The final-scroll-close approval policy requires a reason (and the API's
      // reason field is min_length 1), so always send a non-empty one — otherwise a
      // balanced close with no remarks 422s and the counter never closes.
      const payload = {
        declared_cash_amount: Number(declaredCash || 0).toFixed(2),
        reason: (closingRemarks && closingRemarks.trim()) || "Counter shift close",
      };
      const r = await run(() => finalizeScroll(finalScrollId, payload), "Session closed and scroll finalized.");
      if (r.ok) {
        setSession((s) => (s ? { ...s, status: "CLOSED" } : s));
        loadCounters(); // counter is free again — release it for other cashiers
      }
      return r;
    },
    [finalScrollId, session, loadCounters, run]
  );

  // Note: there is deliberately no "clear session" — the stored session_id is
  // never deleted (no endpoint to recover it), only overwritten by a new open.

  return {
    counterCode,
    setCounterCode: selectCounter,
    counters,
    refreshCounters: loadCounters,
    lockedByOther,
    lockedByLabel,
    session,
    scroll,
    finalScrollId,
    printJob,
    busy,
    error,
    canRetry,
    retry,
    openSession,
    refreshInterim,
    printInterim,
    printFinal,
    generateFinal,
    finalize,
    systemCash: systemCashOf(scroll),
  };
}
